// UniqueWaysModal — the marketing-page "Unique ways to utilize My Silo" layer
// that opens from the blue link inside the welcome / Finish-Setup explainer.
// A vivid, scannable list of digital-twin use cases. Each title is its own
// accordion item: all collapsed by default, only ONE open at a time, and the
// open item's description is centered + scrolled into view. (Randy, Jun 2026)
//
// Jul 2026 (Randy) — UNIQUE_WAYS moved out to `@/lib/waysToUse` so this
// modal AND the four smaller per-pill modals in CreateSiloDialog share
// one source of truth. Idea copy is defined once, in one place.
import { useRef, useState } from "react";
import { X, CaretDown } from "@phosphor-icons/react";
import { UNIQUE_WAYS } from "@/lib/waysToUse";

export { UNIQUE_WAYS }; // legacy re-export for anything that still imports from here

export default function UniqueWaysModal({ onClose }) {
  const [openIdx, setOpenIdx] = useState(null);
  const itemRefs = useRef({});

  const toggle = (i) => {
    setOpenIdx((cur) => {
      const next = cur === i ? null : i;
      if (next === i) {
        setTimeout(() => {
          itemRefs.current[i]?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 60);
      }
      return next;
    });
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="unique-ways-modal"
    >
      <div className="absolute inset-0 bg-[#1C1B1A]/55 backdrop-blur-sm" />
      <div
        className="relative z-10 bg-[#FDFCF8] rounded-2xl max-w-xl w-full max-h-[88dvh] flex flex-col shadow-2xl border hairline"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-6 sm:px-7 pt-6 pb-4 shrink-0 border-b hairline">
          <div>
            <h3 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">
              Unique ways to use My Silo
            </h3>
            <p className="text-[13px] text-[#6B6A68] mt-1">
              Tap any idea to see how a digital twin brings it to life.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="unique-ways-close"
            className="text-[#6B6A68] hover:text-[#1C1B1A] p-1 shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-3 sm:px-4 py-3">
          {UNIQUE_WAYS.map((w, i) => {
            const open = openIdx === i;
            return (
              <div
                key={i}
                ref={(el) => { itemRefs.current[i] = el; }}
                className={`rounded-xl mb-1.5 transition-colors ${open ? "bg-white border hairline shadow-sm" : "hover:bg-white/60"}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  data-testid={`unique-way-item-${i}`}
                  aria-expanded={open}
                  className="w-full flex items-center justify-between gap-3 text-left px-4 py-3"
                >
                  <span className={`text-[15px] ${open ? "font-semibold text-[#1C1B1A]" : "font-medium text-[#3A3A38]"}`}>
                    {w.title}
                  </span>
                  <CaretDown
                    size={16}
                    weight="bold"
                    className={`shrink-0 text-[#4A5D4E] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                  />
                </button>
                {open && (
                  <p
                    data-testid={`unique-way-body-${i}`}
                    className="px-5 sm:px-7 pb-5 pt-1 text-[13.5px] leading-relaxed text-[#4A4A48] text-center whitespace-pre-line"
                  >
                    {w.body}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
